// components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import CommunicationDialog from './CommunicationDialog';

// hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useConnectEmployeeToFlexMatchRequest from '../hooks/useConnectEmployeeToFlexMatchRequest';
import { usePreferredEmployeeActions } from '../hooks/usePreferredEmployeeActions';

// types
import type { GridValidRowModel } from '@mui/x-data-grid-pro';
import type { PreferredEmployee } from 'components/shared/PreferredEmployees.types';
import type { EmployeesTableProps } from './EmployeesTable.types';

export const EmployeesTable = ({
  selectedRow,
  data,
  isLoading,
  columns,
  rowId,
  isCandidatesTable,
  onClose,
  isOpen = true
}: EmployeesTableProps) => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const { t: tCommon } = useTranslation('common');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  const [openCommunicationDialog, setOpenCommunicationDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] =
    useState<GridValidRowModel | null>(null);
  const [isPhoneMessage, setIsPhoneMessage] = useState(false);

  const { addEmployeeFor } = usePreferredEmployeeActions();
  const { mutateAsync: connectEmployeeToFlexMatchRequest } =
    useConnectEmployeeToFlexMatchRequest({ isWebSocketUpdate: true });

  const handleAddEmployee = addEmployeeFor(selectedRow, isOpen);

  const handleOpenCommunicationDialog = (
    row: GridValidRowModel | undefined,
    isPhoneMessage = false
  ) => {
    setSelectedEmployee(row ?? null);
    setIsPhoneMessage(isPhoneMessage);
    setOpenCommunicationDialog(true);
  };

  const handleCloseCommunicationDialog = () => {
    setOpenCommunicationDialog(false);
    setSelectedEmployee(null);
  };

  if (!selectedRow || !data || !columns || !rowId) return null;

  const handleConnectEmployee = async (row?: GridValidRowModel) => {
    if (!row) return;

    const { employeeId, workAgreementId } = row;

    await connectEmployeeToFlexMatchRequest(
      {
        taskId: selectedRow.taskId,
        body: {
          formdata: {
            actions: 'connect',
            employeeId,
            workAgreementId
          }
        }
      },
      {
        onSuccess: () => {
          onClose?.();
        }
      }
    );
  };

  return (
    <>
      <TableCRUDLayout
        columns={columns}
        rows={data}
        getRowId={rowId}
        dynamicHeight
        editable={false}
        loading={isLoading}
        pagination
        pageSize={paginationModel.pageSize}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={({ pageSize }) =>
          setPaginationModel({ ...paginationModel, pageSize })
        }
        actionsThreshold={4}
        customActions={(_, row) => [
          {
            tooltipTitle: tCommon('email'),
            icon: 'envelope',
            onClick: () => handleOpenCommunicationDialog(row),
            hidden: !row?.askedTaskId || !row?.comEmail
          },
          {
            tooltipTitle: tCommon('messages'),
            icon: 'comments',
            onClick: () => handleOpenCommunicationDialog(row, true),
            hidden: !row?.askedTaskId || !row?.comPhone
          },
          {
            tooltipTitle: t('preference'),
            icon: 'star-outline',
            onClick: (_id, _row, _func, event) => {
              event?.stopPropagation();
              handleAddEmployee(_row as PreferredEmployee);
            },
            hidden: isCandidatesTable
          },
          {
            tooltipTitle: t('link'),
            icon: 'link',
            onClick: () => handleConnectEmployee(row)
          }
        ]}
      />

      <CommunicationDialog
        isOpen={openCommunicationDialog}
        onClose={handleCloseCommunicationDialog}
        selectedEmployee={selectedEmployee}
        selectedRow={selectedRow}
        isPhoneMessage={isPhoneMessage}
      />
    </>
  );
};
